import {noop} from '@benchling/js-utils';
import {Space, Button, Checkbox, createStyles, Divider, Group, Paper, Text} from '@mantine/core';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ChemicalFormatType, EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {
  ChemicalStructureDrawerMode,
  useControlledChemicalStructureDrawer,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import ChemicalCounterpartFields from 'chemistry/molecules/ChemicalCounterpartFields';
import ChemicalStructureModalInput from 'chemistry/molecules/ChemicalStructureModalInput';
import {createMolecule} from 'chemistry/molecules/CreateMoleculeUtil';
import type * as MoleculeDAO from 'chemistry/molecules/MoleculeDAO';
import {SchemaInfoBanner, SchemaMismatchAlert} from 'chemistry/molecules/SchemaMismatchBanners';
import useCounterpartDetection from 'chemistry/molecules/useCounterpartDetection';
import {UseSchemaMismatchDetectionReturn} from 'chemistry/molecules/useSchemaMismatchDetection';
import useSchemaMismatchDetection, {
  MismatchStatus,
  SelectedSchemaRole,
} from 'chemistry/molecules/useSchemaMismatchDetection';
import {COMPOUND_FIELD_SYSTEM_NAME} from 'chemistry/schema-configuration/MoleculeSchemaInterfaceConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isLabelingStrategy, LABELING_STRATEGIES_REQUIRING_NAME} from 'constants/RegistryConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import HelpButton from 'help/HelpButton';
import CreateBioentityTagInputs, {
  useCreateBioentityTagInputs,
} from 'registry/create-bioentity/CreateBioentityTagInputs';
import QueryingSchemaLabelingStrategyInput from 'registry/labeling-strategies/SchemaLabelingStrategyInput';
import RegistrySelector from 'registry/RegistrySelector';
import TagSchemaSelector, {TagSchemaSelectorProps} from 'schemas/shared/TagSchemaSelector';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import useDisabledHorizontalOverscroll from 'ui-library/hooks/useDisabledHorizontalOverscroll';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import ModalFooter from 'ui-library/modals/ModalFooter';
import * as keyboard from 'util/Keyboard';
import {withLoadingScreen} from 'util/Promises';
import {ElementKeyboardEvent} from 'util/Types';

// These styles are mainly to properly setup scrolling behavior for the modal content.
// Our usage of Mantine and runModal create a lot of quirks by default.
const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    overscrollBehaviorX: 'none',
  },
  scrollViewport: {
    flex: 1,
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    overscrollBehaviorX: 'none',
    touchAction: 'pan-y',
  },
  scrollContent: {
    padding: theme.spacing.lg,
  },
  footer: {
    flexShrink: 0,
    padding: theme.spacing.lg,
  },
  structurePaper: {
    backgroundColor: theme.colors.blue[0],
  },
  registerCheckboxLabel: {
    alignSelf: 'flex-start',
  },
}));

const CHEMICAL_FORMAT_TO_MARVIN: Record<ChemicalFormatType, MarvinChemicalFormat> = {
  [ChemicalFormatType.SMILES]: MarvinChemicalFormat.SMILES,
  [ChemicalFormatType.MOLFILE]: MarvinChemicalFormat.MOL,
};

function getSchemaMetadataFn(
  pairingMap: UseSchemaMismatchDetectionReturn['pairingMap']
): TagSchemaSelectorProps['getSchemaMetadata'] {
  if (!BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS) {
    return undefined;
  }
  return (schema: {id: string}): string | undefined => {
    const pairing = pairingMap[schema.id];
    if (!pairing) {
      return undefined;
    }
    if (pairing.formSchemaId === schema.id) {
      return `Linked to: ${pairing.compoundSchemaName}`;
    }
    return `Linked to: ${pairing.formSchemaName}`;
  };
}

interface CreateMoleculeModalProps {
  onMoleculeCreated?: (
    moleculeId: string,
    moleculeName: string,
    parentMolecule?: MoleculeDAO.CreatedParentMolecule
  ) => void;
  close: () => void;
  initialChemicalStructure?: {value: string; format: ChemicalFormatType};
  initialFolderId: string | null;
  initialName?: string;
}

export default function CreateMoleculeModal({
  onMoleculeCreated,
  close,
  initialChemicalStructure,
  initialFolderId,
  initialName,
}: CreateMoleculeModalProps): JSX.Element {
  const {classes} = useStyles();
  const useNewCreatePath = BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS;
  const showRegisterOnCreate =
    BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS &&
    BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_REGISTER_ON_CREATE;
  // lockScroll is false on this modal (for nested FolderSelector), so block horizontal overscroll
  // gestures from reaching the page behind the modal.
  useDisabledHorizontalOverscroll();
  const [name, setName] = useState<string>(initialName ?? '');
  const [folderId, setFolderId] = useState<string | null>(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null
  );
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>([]);
  const [registerAfterCreation, setRegisterAfterCreation] = useState(false);
  const [namingStrategy, setNamingStrategy] = useState<RegistryLabelingStrategy | null>(null);
  const [sketcherValue, setSketcherValue] = useControlledChemicalStructureDrawer();
  const [posting, setPosting] = useState(false);
  const [tagSchema, tags, schemaSelectorProps, tagInputsProps] = useCreateBioentityTagInputs();

  const counterpartDetection = useCounterpartDetection(
    sketcherValue,
    tagSchema?.id,
    BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS
  );

  const {mismatchResult, pairingMap} = useSchemaMismatchDetection({
    tagSchemaId: BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS ? (tagSchema?.id ?? null) : null,
    hasCounterparts: counterpartDetection.hasCounterparts,
    registryId: tagSchema?.registryId ?? null,
  });

  const isFormSchemaSelected =
    mismatchResult.status === MismatchStatus.OK &&
    mismatchResult.selectedSchemaRole === SelectedSchemaRole.FORM;

  const visibleTagInputsProps = isFormSchemaSelected
    ? {
        ...tagInputsProps,
        tags: tagInputsProps.tags.filter(
          (tag) => tag.tagSchemaField.systemName !== COMPOUND_FIELD_SYSTEM_NAME
        ),
      }
    : tagInputsProps;

  const isRegistering = showRegisterOnCreate && registerAfterCreation;

  const isNameDisabled =
    isRegistering && namingStrategy != null && !LABELING_STRATEGIES_REQUIRING_NAME.includes(namingStrategy);

  const isRegistrationIncomplete = isRegistering && (namingStrategy == null || tagSchema?.registryId == null);

  const isFolderRequired = !isRegistering;

  const disabled =
    (!isNameDisabled && name.trim().length === 0) ||
    (isFolderRequired && folderId == null) ||
    isRegistrationIncomplete ||
    posting ||
    counterpartDetection.hasBlockingError ||
    counterpartDetection.isParsing ||
    mismatchResult.status === MismatchStatus.BLOCKED ||
    (useNewCreatePath && tagSchema == null);

  function handleSubmit(): void {
    if (disabled) {
      return;
    }
    handleCreateMolecule();
  }

  function handleKeyDown(e: ElementKeyboardEvent): void {
    if (keyboard.isOSCtrlKey(e) && keyboard.isEnterKey(e)) {
      e.preventDefault();
      handleSubmit();
    }
  }

  async function handleCreateMolecule(): Promise<void> {
    if (!isRegistering) {
      assert(folderId != null, 'Unexpected creating molecule with null folderId');
    }
    setPosting(true);

    try {
      const molecule = await withLoadingScreen(
        createMolecule({
          name,
          folderId,
          sketcherValue,
          tagSchema,
          tags,
          studySearchItems,
          registerAfterCreation: isRegistering,
          namingStrategy,
        }),
        'Creating molecule...'
      );
      if (onMoleculeCreated) {
        onMoleculeCreated(molecule.id, molecule.name, molecule.parentMolecule);
      } else {
        FileBrowserActions.openFile(molecule.id);
      }
      close();
      if (folderId != null) {
        FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId);
      }
    } finally {
      setPosting(false);
    }
  }

  return (
    <form
      className={classes.container}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      onKeyDown={handleKeyDown}
    >
      <div className={classes.scrollViewport}>
        <div className={classes.scrollContent}>
          <Row>
            <Col sm={12}>
              <FormGroup>
                <FieldLabel label="Entity name" isRequired={!isNameDisabled} />
                <input
                  autoFocus={true}
                  type="text"
                  value={isNameDisabled ? '' : name}
                  disabled={isNameDisabled}
                  placeholder="Name"
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  {...getTestProps({component: 'CreateMoleculeModal', element: 'input', key: 'entityName'})}
                  data-amp-safe={false}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm={BenchlingConfig.STUDIES_ENABLE ? 6 : 12}>
              <FormGroup>
                <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={isFolderRequired} />
                <FolderSelector
                  buttonSize={ButtonSize.MEDIUM}
                  enableRedesign={true}
                  showInventory={true}
                  value={folderId}
                  onChange={(e) => {
                    setFolderId(e.target.value);
                  }}
                  unsetOnFetchFail={true}
                />
              </FormGroup>
            </Col>
            {BenchlingConfig.STUDIES_ENABLE && (
              <Col sm={6}>
                <FormGroup>
                  <FieldLabel label="Studies" />
                  <StudySearchInput
                    onChange={(studySearchItems: Array<StudySearchItem>) => {
                      setStudySearchItems(studySearchItems);
                    }}
                    shouldAllowMultiple={true}
                    selectedItems={studySearchItems}
                  />
                </FormGroup>
              </Col>
            )}
          </Row>
          <Row>
            <Col sm={12}>
              <FormGroup>
                <FieldLabel
                  label="Schema"
                  isRequired={useNewCreatePath}
                  dataTestAttrs={{component: 'CreateMoleculeModal', element: 'schemaLabel'}}
                />
                <TagSchemaSelector
                  allowNone={!useNewCreatePath}
                  oligoAwareFolderItemTypes={[ModelTypeConstants.MOLECULE]}
                  getSchemaMetadata={getSchemaMetadataFn(pairingMap)}
                  {...schemaSelectorProps}
                />
              </FormGroup>
              <FormGroup>
                <CreateBioentityTagInputs {...visibleTagInputsProps} />
              </FormGroup>
            </Col>
          </Row>
          {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && (
            <>
              <SchemaMismatchAlert mismatchResult={mismatchResult} />
              <SchemaInfoBanner mismatchResult={mismatchResult} />
            </>
          )}
          <Paper className={classes.structurePaper} radius="sm" p="md">
            <ChemicalStructureModalInput
              initialSketcherValue={initialChemicalStructure?.value ?? EMPTY_MOLFILE}
              onSketcherValueChange={setSketcherValue}
              mode={ChemicalStructureDrawerMode.BASIC_SEARCH}
              showAttachmentPointBanner={false}
              inputFormat={
                initialChemicalStructure
                  ? (CHEMICAL_FORMAT_TO_MARVIN[initialChemicalStructure.format] ?? null)
                  : null
              }
              outputFormat={MarvinChemicalFormat.MOLV3}
            />
            {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && (
              <>
                <Divider my="lg" size="xs" />
                <ChemicalCounterpartFields
                  parseResponse={counterpartDetection.parseResponse}
                  isLoading={counterpartDetection.isLoading}
                  error={counterpartDetection.error}
                  errorCode={counterpartDetection.errorCode}
                  entityName={name.trim() || undefined}
                />
              </>
            )}
          </Paper>
        </div>
      </div>
      <ModalFooter sticky={false} className={classes.footer}>
        {showRegisterOnCreate && registerAfterCreation && (
          <div role="group" aria-label="Registry settings">
            <Row>
              <Col sm={6}>
                <FormGroup>
                  <FieldLabel label="Registry" />
                  {tagSchema?.registryId == null ? (
                    <SelectDropdown displayName="Select a schema first" disabled={true} menu={<></>} />
                  ) : (
                    <RegistrySelector disabled={true} onChange={noop} registryId={tagSchema?.registryId} />
                  )}
                </FormGroup>
              </Col>
              <Col sm={6}>
                <QueryingSchemaLabelingStrategyInput
                  labelingStrategy={namingStrategy}
                  onChange={(strategy) => {
                    if (isLabelingStrategy(strategy)) {
                      setNamingStrategy(strategy);
                    }
                  }}
                  tagSchemaId={tagSchema?.id}
                  disabled={tagSchema?.id == null}
                />
              </Col>
            </Row>
            <Divider />
            <Space h="lg" />
          </div>
        )}
        <Group position={showRegisterOnCreate ? 'apart' : 'right'}>
          {showRegisterOnCreate && (
            <Group>
              <Checkbox
                checked={registerAfterCreation}
                label={
                  <>
                    <Text>Register this molecule after creation</Text>
                  </>
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                  setRegisterAfterCreation(event.currentTarget.checked);
                }}
                classNames={{
                  label: classes.registerCheckboxLabel,
                }}
              />
              <HelpButton
                interaction={FloatingInteraction.HOVER}
                renderContent={() => (
                  <div>
                    Register this molecule to track it across your organization after it's been created.
                  </div>
                )}
              />
            </Group>
          )}
          <Button type="submit" disabled={disabled}>
            Create
          </Button>
        </Group>
      </ModalFooter>
    </form>
  );
}
